import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import { useTheme } from '../ThemeContext';
import './Navigation.css';
import { useConfig } from '../ConfigContext';

function Navigation() {
  const config = useConfig();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const location = useLocation();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const isPrymePage = location.pathname === '/pryme';
  const isApexPage = location.pathname === '/apex';
  const navbarRef = useRef(null);

  // Set body background based on current page and theme
  useEffect(() => {
    // For Pryme/Apex pages in light mode, use white/teal gradient
    if ((isPrymePage || isApexPage) && !isDarkMode) {
      document.body.style.background = `
        radial-gradient(ellipse at 20% 30%, rgba(47, 196, 176, 0.2) 0%, transparent 40%),
        radial-gradient(ellipse at 80% 60%, rgba(47, 196, 176, 0.15) 0%, transparent 35%),
        radial-gradient(ellipse at 50% 80%, rgba(47, 196, 176, 0.17) 0%, transparent 38%),
        radial-gradient(ellipse at 70% 20%, rgba(47, 196, 176, 0.12) 0%, transparent 30%),
        #ffffff
      `;
    } else if (isPrymePage || isApexPage) {
      // For Pryme/Apex pages in dark mode, use primary/white gradient
      document.body.style.background = `
        radial-gradient(ellipse at 20% 30%, rgba(255, 255, 255, 0.25) 0%, transparent 55%),
        radial-gradient(ellipse at 80% 60%, rgba(255, 255, 255, 0.18) 0%, transparent 50%),
        radial-gradient(ellipse at 50% 80%, rgba(255, 255, 255, 0.22) 0%, transparent 55%),
        radial-gradient(ellipse at 70% 20%, rgba(255, 255, 255, 0.15) 0%, transparent 50%),
        #1E2D56
      `;
    } else {
      // For other pages, use white/teal gradient
      document.body.style.background = `
        radial-gradient(ellipse at 20% 30%, rgba(47, 196, 176, 0.2) 0%, transparent 40%),
        radial-gradient(ellipse at 80% 60%, rgba(47, 196, 176, 0.15) 0%, transparent 35%),
        radial-gradient(ellipse at 50% 80%, rgba(47, 196, 176, 0.17) 0%, transparent 38%),
        radial-gradient(ellipse at 70% 20%, rgba(47, 196, 176, 0.12) 0%, transparent 30%),
        #ffffff
      `;
    }
    document.body.style.backgroundAttachment = 'fixed';
  }, [isPrymePage, isApexPage, isDarkMode]);

  // Close mobile menu when location changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setExpandedSection(null);
  }, [location.pathname]);

  // Add mouse tracking for glass navbar light effect
  useEffect(() => {
    const navbar = navbarRef.current;
    if (!navbar) return;

    const handleMouseMove = (e) => {
      const rect = navbar.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      navbar.style.setProperty('--mouse-x', `${x}px`);
      navbar.style.setProperty('--mouse-y', `${y}px`);
    };

    navbar.addEventListener('mousemove', handleMouseMove);
    return () => navbar.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <nav ref={navbarRef} className="navbar glass-navbar">
      <div className="nav-container">
        <Link to="/" className="nav-logo" style={{ fontSize: config.fontSizes.navbar.logo }}>
          <img 
            src="/images/general/TytonLogo-transparent.png"
            alt="Tytonvoice logo" 
            className="nav-logo-img" 
          />
          <span>Tytonvoice.ai</span>
        </Link>
        
        <ul className="nav-menu">
          {config.navbar.products.enabled && (
            <li className="nav-item dropdown">
              <button className="nav-link" style={{ fontSize: config.fontSizes.navbar.link }}>
                Products
                <span className="dropdown-arrow">▾</span>
              </button>
              <div className="dropdown-menu products-dropdown">
                {config.navbar.products.items.productNames && (
                  <>
                    <div className="dropdown-section">
                      <div className="dropdown-section-title" style={{ fontSize: config.fontSizes.navbar.dropdownTitle }}>Products</div>
                      <Link to="/pryme" className="nav-dropdown-link" style={{ fontSize: config.fontSizes.navbar.dropdownItem }}>Pryme</Link>
                      <Link to="/apex" className="nav-dropdown-link" style={{ fontSize: config.fontSizes.navbar.dropdownItem }}>Apex</Link>
                    </div>
                    <div className="dropdown-divider"></div>
                  </>
                )}
                {config.navbar.products.items.platforms && (
                  <>
                    <div className="dropdown-section">
                      <div className="dropdown-section-title" style={{ fontSize: config.fontSizes.navbar.dropdownTitle }}>Platforms</div>
                      <Link to="/ondevice" className="nav-dropdown-link" style={{ fontSize: config.fontSizes.navbar.dropdownItem }}>On-Device</Link>
                      <Link to="/cloud" className="nav-dropdown-link" style={{ fontSize: config.fontSizes.navbar.dropdownItem }}>Cloud</Link>
                    </div>
                    <div className="dropdown-divider"></div>
                  </>
                )}
                {config.navbar.products.items.whyTytonvoice && (
                  <div className="dropdown-section">
                    <div className="dropdown-section-title" style={{ fontSize: config.fontSizes.navbar.dropdownTitle }}>Why Tytonvoice</div>
                    <Link to="/benchmarks" className="nav-dropdown-link" style={{ fontSize: config.fontSizes.navbar.dropdownItem }}>Benchmarks</Link>
                    <button type="button" className="nav-dropdown-link" style={{ fontSize: config.fontSizes.navbar.dropdownItem }} onClick={() => window.open(config.navbar.aboutUs.url, '_blank')}>About Us</button>
                  </div>
                )}
              </div>
            </li>
          )}
          {config.navbar.partners.enabled && (
            <li className="nav-item">
              <a href={config.navbar.partners.url} className="nav-link" style={{ fontSize: config.fontSizes.navbar.link }} target="_blank" rel="noopener noreferrer">
                Partners
              </a>
            </li>
          )}
          <li className="nav-item">
            <Link to="/markets" className="nav-link" style={{ fontSize: config.fontSizes.navbar.link }}>
              Markets
            </Link>
          </li>
          {config.navbar.pricing.enabled && (
            <li className="nav-item">
              <button type="button" className="nav-link" style={{ fontSize: config.fontSizes.navbar.link }}>Pricing</button>
            </li>
          )}
          {config.navbar.resources.enabled && (
            <li className="nav-item dropdown">
              <button className="nav-link" style={{ fontSize: config.fontSizes.navbar.link }}>
                Resources
                <span className="dropdown-arrow">▾</span>
              </button>
              <div className="dropdown-menu">
                {config.navbar.resources.items.documentation && <button type="button" style={{ fontSize: config.fontSizes.navbar.dropdownItem }}>Documentation</button>}
                {config.navbar.resources.items.blog && <button type="button" style={{ fontSize: config.fontSizes.navbar.dropdownItem }}>Blog</button>}
                {config.navbar.resources.items.support && <button type="button" style={{ fontSize: config.fontSizes.navbar.dropdownItem }}>Support</button>}
              </div>
            </li>
          )}
        </ul>

        {/* Right side: Contact Us + Theme Toggle + Mobile Menu */}
        <div className="nav-right-group">
          <a
            href={config.navbar.contactUs.url}
            target="_blank"
            rel="noopener noreferrer"
            className="nav-cta-bubble"
            style={{ fontSize: config.fontSizes.navbar.ctaButton }}
          >
            Contact Us
          </a>
          
          <ThemeToggle />

          {/* Mobile hamburger menu button */}
          <button 
            className="mobile-menu-button" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      {mobileMenuOpen && (
        <div className="mobile-dropdown-menu">
          {config.navbar.products.enabled && (
            <div className="mobile-menu-item">
              <button 
                className={`mobile-menu-button-main ${expandedSection === 'products' ? 'expanded' : ''}`}
                onClick={() => toggleSection('products')}
              >
                <span>Products</span>
                <span className="mobile-menu-arrow">▾</span>
              </button>
              {expandedSection === 'products' && (
                <div className="mobile-menu-expanded">
                  {config.navbar.products.items.productNames && (
                    <>
                      <div className="mobile-expanded-section">
                        <div className="mobile-expanded-title">Products</div>
                        <Link to="/pryme" className="mobile-expanded-item">Pryme</Link>
                        <Link to="/apex" className="mobile-expanded-item">Apex</Link>
                      </div>
                    </>
                  )}
                  {config.navbar.products.items.platforms && (
                    <div className="mobile-expanded-section">
                      <div className="mobile-expanded-title">Platforms</div>
                      <Link to="/ondevice" className="mobile-expanded-item" onClick={() => setMobileMenuOpen(false)}>On-Device</Link>
                      <Link to="/cloud" className="mobile-expanded-item" onClick={() => setMobileMenuOpen(false)}>Cloud</Link>
                    </div>
                  )}
                  {config.navbar.products.items.whyTytonvoice && (
                    <div className="mobile-expanded-section">
                      <div className="mobile-expanded-title">Why Tytonvoice</div>
                      <Link to="/benchmarks" className="mobile-expanded-item" onClick={() => setMobileMenuOpen(false)}>Benchmarks</Link>
                      <button type="button" className="mobile-expanded-item" onClick={() => window.open(config.navbar.aboutUs.url, '_blank')}>About Us</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {config.navbar.partners.enabled && (
            <div className="mobile-menu-item">
              <a href={config.navbar.partners.url} className="mobile-menu-button-main" target="_blank" rel="noopener noreferrer">
                <span>Partners</span>
              </a>
            </div>
          )}
          <div className="mobile-menu-item">
            <Link to="/markets" className="mobile-menu-button-main" onClick={() => setMobileMenuOpen(false)}>
              <span>Markets</span>
            </Link>
          </div>
          
          {config.navbar.pricing.enabled && (
            <div className="mobile-menu-item">
              <button type="button" className="mobile-menu-button-main">
                <span>Pricing</span>
              </button>
            </div>
          )}
          
          {config.navbar.resources.enabled && (
            <div className="mobile-menu-item">
              <button 
                className={`mobile-menu-button-main ${expandedSection === 'resources' ? 'expanded' : ''}`}
                onClick={() => toggleSection('resources')}
              >
                <span>Resources</span>
                <span className="mobile-menu-arrow">▾</span>
              </button>
              {expandedSection === 'resources' && (
                <div className="mobile-menu-expanded">
                  <div className="mobile-expanded-section">
                    {config.navbar.resources.items.documentation && <button type="button" className="mobile-expanded-item">Documentation</button>}
                    {config.navbar.resources.items.blog && <button type="button" className="mobile-expanded-item">Blog</button>}
                    {config.navbar.resources.items.support && <button type="button" className="mobile-expanded-item">Support</button>}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </nav>
  );
}

export default Navigation;